import { defineStore } from 'pinia';
import { useContextStore } from '@/Stores/context';

type BookmarksByExperience = Record<string, string[]>;

type BookmarkState = {
  byExperience: BookmarksByExperience;
};

export type BookmarkListLayout = 'card' | 'list' | 'compact';
export type BookmarkListMapMode = 'full' | 'half' | 'hide';
export type BookmarkChangeEventDetail =
  { globalId: string; action: 'added' | 'removed' } | { action: 'cleared' };

export const BOOKMARK_CHANGE_EVENT = 'bookmark-change';

export type BookmarkListProps = {
  layout?: BookmarkListLayout;
  map?: BookmarkListMapMode;
  limit?: number | string;
};

export const DEFAULT_BOOKMARK_LIST_PROPS = {
  layout: 'card',
  map: 'half',
  limit: 12,
} as const satisfies Required<BookmarkListProps>;

const STORAGE_KEY = 'pages.bookmarks';

function dispatchBookmarkChange(detail: BookmarkChangeEventDetail): void {
  window.dispatchEvent(
    new CustomEvent<BookmarkChangeEventDetail>(BOOKMARK_CHANGE_EVENT, {
      detail,
    }),
  );
}

function readLocalAll(): { byExperience: BookmarksByExperience } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { byExperience: {} };
    const parsed = JSON.parse(raw);

    if (parsed && parsed.byExperience !== null) {
      return { byExperience: parsed.byExperience as BookmarksByExperience };
    }

    return { byExperience: {} };
  } catch {
    return { byExperience: {} };
  }
}

function writeLocalAll(byExperience: BookmarksByExperience): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ byExperience }));
  } catch {
    /* ignore */
  }
}

export const useBookmarksStore = defineStore('pages-bookmarks', {
  state: (): BookmarkState => ({
    byExperience: {},
  }),
  getters: {
    currentItems(state): string[] {
      const exp = useContextStore().experience;
      return state.byExperience[exp] ?? [];
    },
    count(): number {
      return this.currentItems.length;
    },
    has: (s) => (id: string) => {
      const exp = useContextStore().experience;
      const list = s.byExperience[exp] ?? [];
      return list.includes(String(id));
    },
  },
  actions: {
    ensureExp(exp: string): void {
      if (!this.byExperience[exp]) this.byExperience[exp] = [];
    },
    load(): void {
      const { byExperience } = readLocalAll();
      this.byExperience = byExperience || {};
      const exp = useContextStore().experience;
      this.ensureExp(exp);
    },
    save(): void {
      writeLocalAll(this.byExperience);
    },
    syncFromLocalStorage(): void {
      this.load();
    },
    syncToLocalStorage(): void {
      this.save();
    },
    add(id: string): void {
      const key = String(id);
      const exp = useContextStore().experience;
      this.ensureExp(exp);
      const list = this.byExperience[exp];
      if (!list.includes(key)) {
        list.push(key);
        this.save();
        dispatchBookmarkChange({ globalId: key, action: 'added' });
      }
    },
    remove(id: string): void {
      const key = String(id);
      const exp = useContextStore().experience;
      this.ensureExp(exp);
      this.byExperience[exp] = this.byExperience[exp].filter((i) => i !== key);
      this.save();
      dispatchBookmarkChange({ globalId: key, action: 'removed' });
    },
    toggle(id: string): void {
      const key = String(id);
      const exp = useContextStore().experience;
      this.ensureExp(exp);
      const list = this.byExperience[exp];
      let action: 'added' | 'removed';
      if (list.includes(key)) {
        this.byExperience[exp] = list.filter((i) => i !== key);
        action = 'removed';
      } else {
        list.push(key);
        action = 'added';
      }
      this.save();
      dispatchBookmarkChange({ globalId: key, action });
    },
    clear(): void {
      const exp = useContextStore().experience;
      this.ensureExp(exp);
      this.byExperience[exp] = [];
      this.save();
      dispatchBookmarkChange({ action: 'cleared' });
    },
    replaceAll(ids: string[]): void {
      const exp = useContextStore().experience;
      this.ensureExp(exp);
      this.byExperience[exp] = Array.from(new Set(ids.map(String)));
      this.save();
    },
  },
});
